import { atom } from 'jotai';

import { patchTemplateSettingMutationAtom } from '../_graphql/mutations/corePatchScopedSettingValuesMutation';
import { fileBulkDeleteMutationAtom } from '../_graphql/mutations/fileBulkDeleteMutation';
import { fileCancelPendingUploadMutationAtom } from '../_graphql/mutations/fileCancelPendingUploadMutation';
import { fileConfirmUploadMutationAtom } from '../_graphql/mutations/fileConfirmUploadMutation';
import { fileCreateMutationAtom } from '../_graphql/mutations/fileCreateMutation';
import { fileCreateZipMutationAtom } from '../_graphql/mutations/fileCreateZipMutation';
import { fileDeleteMutationAtom } from '../_graphql/mutations/fileDeleteMutation';
import { fileMoveMutationAtom } from '../_graphql/mutations/fileMoveMutation';
import { fileRenameMutationAtom } from '../_graphql/mutations/fileRenameMutation';
import { fileUpdateMetadataMutationAtom } from '../_graphql/mutations/fileUpdateMetadataMutation';
import { uploadFileToS3, UploadProgress } from '../utils/s3';

import {
  $currentFolderId,
  $remoteFiles,
  $exitSelectionMode,
} from './files.state';
import { $directoryList } from './fileTree.state';

import { $user } from '~/shared/providers/auth/identity.localState';

// 🟠🟠🟠 ACTION -> CREATE file
export const $createFile = fileCreateMutationAtom({
  onSuccess: (get) => {
    // Refetch files after creating file
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Refresh tree directories
    const directoryListQuery = get($directoryList);
    directoryListQuery.refetch();

    // If we're in a folder, the files query will automatically update
    const currentFolderId = get($currentFolderId);
    if (currentFolderId) {
      // The reactive queries will automatically update when directories are refetched
    }
  },
});

// 🟠🟠🟠 ACTION -> CREATE folder
export const $createFolder = fileCreateMutationAtom({
  onSuccess: (get, _set) => {
    // Refetch files after creating folder
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Refresh tree directories immediately
    const directoryListQuery = get($directoryList);
    directoryListQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> CONFIRM upload
export const $confirmUpload = fileConfirmUploadMutationAtom({
  onSuccess: (get) => {
    // Refetch files after confirming upload
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> CANCEL pending upload
export const $cancelPendingUpload = fileCancelPendingUploadMutationAtom({
  onSuccess: (get) => {
    // Refetch files after canceling upload
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> DELETE file/folder
export const $deleteFile = fileDeleteMutationAtom({
  onSuccess: (get, _set) => {
    // Refetch files after deletion
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Refresh tree directories
    const directoryListQuery = get($directoryList);
    directoryListQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> MOVE file/folder
export const $moveFile = fileMoveMutationAtom({
  onSuccess: (get, _set) => {
    // Refetch files after moving
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Refresh tree directories
    const directoryListQuery = get($directoryList);
    directoryListQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> UPDATE file metadata (rename)
export const $updateFileMetadata = fileUpdateMetadataMutationAtom({
  onSuccess: (get, _set) => {
    // Refetch files after updating metadata
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Refresh tree directories for potential folder renames
    const directoryListQuery = get($directoryList);
    directoryListQuery.refetch();

    // Refresh current directory if it's a folder being renamed
    const currentFolderId = get($currentFolderId);
    if (currentFolderId) {
      // set($refreshDirectory, currentFolderId); // This line is removed
    }
  },
});

// 🟠🟠🟠 ACTION -> SET file as template
export const $setFileAsTemplate = patchTemplateSettingMutationAtom({
  onSuccess: (_get, _set, { params }) => {
    console.log('Template setting updated successfully', params);
  },
});

// 🟡🟡🟡 DERIVED ACTION -> Create folder with current parent
export const $createFolderInCurrentDirectory = atom(
  null,
  async (get, set, folderName: string) => {
    const parentId = get($currentFolderId) || undefined;

    await set($createFolder, {
      fileName: folderName,
      fileSize: 0,
      mimeType: 'application/x-folder',
      fileType: 'Directory',
      parentId,
      public: false,
    });
  },
);

// 🟡🟡🟡 DERIVED ACTION -> Upload file to current directory
export const $uploadFileToCurrentDirectory = atom(
  null,
  async (get, set, file: File) => {
    const parentId = get($currentFolderId) || undefined;
    const user = get($user) || undefined;
    let fileId: string | undefined;

    try {
      // Step 1: Create file record and get upload URL
      const createResult = await set($createFile, {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        fileType: 'Regular',
        parentId,
        public: false,
      });

      if (
        !createResult?.filesCreateFile?.uploadUrl ||
        !createResult?.filesCreateFile?.file?.id
      ) {
        throw new Error('Failed to create file record');
      }

      const { uploadUrl } = createResult.filesCreateFile;
      fileId = createResult.filesCreateFile.file.id;

      // Step 2: Upload file to S3
      try {
        const metadata = {
          ...(user?.id && { uploaderId: user.id }),
          ...(createResult?.filesCreateFile?.file?.id && {
            fileId: createResult.filesCreateFile.file.id,
          }),
        };

        await uploadFileToS3(file, metadata, uploadUrl, {
          onProgress: (_progress: UploadProgress) => {
            // NOP
          },
          maxRetries: 3,
          retryDelay: 1000,
        });
      } catch (error) {
        // If upload fails, cancel the pending file
        if (fileId) {
          try {
            await set($cancelPendingUpload, { fileId });
          } catch (cancelError) {
            console.error('Failed to cancel pending file:', cancelError);
          }
        }
        throw new Error(`Failed to upload file: ${error}`);
      }

      // Step 3: Confirm upload
      await set($confirmUpload, {
        fileId,
        checksum: undefined,
      });

      return createResult;
    } catch (error) {
      console.error('Failed to upload file:', error);
      throw error;
    }
  },
);

// 🟠🟠🟠 ACTION -> RENAME file
export const $renameFile = fileRenameMutationAtom({
  onSuccess: (get) => {
    // Refetch files after renaming
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();
  },
});

// 🟠🟠🟠 ACTION -> BULK DELETE files
export const $bulkDeleteFiles = fileBulkDeleteMutationAtom({
  onSuccess: (get, set) => {
    // Refetch files after bulk delete
    const filesQuery = get($remoteFiles);
    filesQuery.refetch();

    // Exit selection mode
    set($exitSelectionMode);
  },
});

// 🟠🟠🟠 ACTION -> BULK DOWNLOAD files (create zip)
export const $bulkDownloadFiles = fileCreateZipMutationAtom({
  onSuccess: (_get, set) => {
    // No refetch needed for download

    // Exit selection mode
    set($exitSelectionMode);
  },
});

// 🟡🟡🟡 DERIVED ACTION -> Bulk move files (client-side loop)
export const $bulkMoveFiles = atom(
  null,
  async (
    _get,
    set,
    { fileIds, newParentId }: { fileIds: string[]; newParentId: string | null },
  ) => {
    const results = {
      successCount: 0,
      failedCount: 0,
      errors: [] as Array<{ fileId: string; error: string }>,
    };

    for (const fileId of fileIds) {
      try {
        await set($moveFile, { fileId, newParentId });
        results.successCount++;
      } catch (error) {
        results.failedCount++;
        results.errors.push({
          fileId,
          error: error instanceof Error ? error.message : String(error),
        });
        console.error(`Failed to move file ${fileId}:`, error);
      }
    }

    // Exit selection mode after all moves complete
    set($exitSelectionMode);

    return results;
  },
);

// 🟡🟡🟡 DERIVED ACTION -> Upload multiple files to current directory
export const $uploadFilesToCurrentDirectory = atom(
  null,
  async (get, set, files: File[]) => {
    const parentId = get($currentFolderId) || undefined;
    const user = get($user) || undefined;

    const results = {
      successful: [] as Array<{ file: File; result: unknown }>,
      failed: [] as Array<{ file: File; error: string }>,
    };

    for (const file of files) {
      let fileId: string | undefined;

      try {
        // Step 1: Create file record and get upload URL
        const createResult = await set($createFile, {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
          fileType: 'Regular',
          parentId,
          public: false,
        });

        if (
          !createResult?.filesCreateFile?.uploadUrl ||
          !createResult?.filesCreateFile?.file?.id
        ) {
          throw new Error('Failed to create file record');
        }

        const { uploadUrl } = createResult.filesCreateFile;
        fileId = createResult.filesCreateFile.file.id;

        // Step 2: Upload file to S3
        try {
          const metadata = {
            ...(user?.id && { uploaderId: user.id }),
            ...(fileId && { fileId }),
          };

          await uploadFileToS3(file, metadata, uploadUrl, {
            onProgress: (_progress: UploadProgress) => {
              // NOP - could be extended to track individual file progress
            },
            maxRetries: 3,
            retryDelay: 1000,
          });
        } catch (error) {
          // If upload fails, cancel the pending file
          if (fileId) {
            try {
              await set($cancelPendingUpload, { fileId });
            } catch (cancelError) {
              console.error('Failed to cancel pending file:', cancelError);
            }
          }
          throw new Error(`Failed to upload file: ${error}`);
        }

        // Step 3: Confirm upload
        await set($confirmUpload, {
          fileId,
          checksum: undefined,
        });

        results.successful.push({ file, result: createResult });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        results.failed.push({
          file,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    return results;
  },
);
